import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { InboxIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";

import { ErrorFallback, Head } from "../../../../components/core";
import { Button } from "../../../../components/form";
import { LimitBy, TableCursor } from "../../../../components/table";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../contexts/NotifyContext";
import { GET_CUSTOMERCONTACTS } from "../../../../graphql/sales/settings/customer-contacts";
import { AlertModal, AlertType, useAlert } from "../../../../hooks/useAlert";
import { formatDate } from "../../../../utils";
import { AuthShield } from "../../../auth/core";

type Customer = {
  id: string;
  customerName: string;
};
type ContactType = {};
type ContactDetails = {
  id: string;
  name: string;
  contactType: ContactType;
  email: string;
  phoneNumber: string;
  sendSms: boolean;
};
type CustomerContact = {
  id: string;
  contactDetails: ContactDetails[];
  createdAt: string;
  customer: Customer;
  emailImportantUpdates: boolean;
  emailSpecials: boolean;
  firstName: string;
  mobileNumber: string;
  phoneNumber: string;
  postalAddress: string;
  postcode: string;
  state: string;
  suburb: string;
  surName: string;
};

const CustomerContactList = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);
  const { addNotify } = useNotifyContext();

  const { data, loading, error, refetch } = useQuery<{
    fetchCustomerContacts: CustomerContact[];
  }>(GET_CUSTOMERCONTACTS);

  const customerContacts = useMemo<CustomerContact[]>(
    () =>
      data?.fetchCustomerContacts.flatMap((item) => ({
        ...item,
        createdAt: formatDate(item.createdAt),
      })) || [],
    [data?.fetchCustomerContacts]
  );

  const DELETE_CUSTOMERCONTACT = gql`
    mutation CustomerContactDelete($id: ID!) {
      customerContactDelete(input: { id: $id }) {
        customerContact {
          id
          firstName
        }
      }
    }
  `;
  const [deleteCustomerContact] = useMutation(DELETE_CUSTOMERCONTACT, {
    refetchQueries: [{ query: GET_CUSTOMERCONTACTS }],
  });
  const handleDelete = async (id: string) => {
    return await deleteCustomerContact({
      variables: {
        id,
      },
    })
      .then(({ data }) => {
        if (data?.customerContactDelete) {
          const deletedCustomerContact =
            data?.customerContactDelete?.customerContact;
          refetch();
          addNotify({
            type: NotifyType.SUCCESS,
            title: `CustomerContact ${deletedCustomerContact.firstName} deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "CustomerContact delete failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "CustomerContact delete failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<CustomerContact, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "firstName",
        header: "Name",
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.firstName,
      },
      {
        accessorKey: "postalAddress",
        header: "Postal Address",
        size: 300,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.postalAddress,
      },
      {
        accessorKey: "state",
        header: "State",
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.state,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        enableHiding: false,
        enableSorting: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) => (
          <div className="flex space-x-2 md:space-x-4">
            <AuthShield access={["update-customers"]}>
              <Button
                type="link"
                href={`/sales/settings/customer-contacts/${props.row.original.id}`}
                variant="icon"
                className="text-blue-500 hover:text-blue-600"
              >
                <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">
                  Edit, {props.row.original.firstName}
                </span>
              </Button>
            </AuthShield>
            <AuthShield access={["destroy-customers"]}>
              <Button
                variant="icon"
                className="text-red-500 hover:text-red-600"
                onClick={() => {
                  setAlert({
                    id: props.row.original.id,
                    name: props.row.original.firstName,
                  });
                }}
              >
                <TrashIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">
                  Delete, {props.row.original.firstName}
                </span>
              </Button>
            </AuthShield>
          </div>
        ),
      },
    ],
    []
  );

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title="Customer Contacts"
        heading="Customer Contacts"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Customer Contacts",
            href: "/sales/settings/customer-contacts",
          },
          {
            name: CustomerContactListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="mb-5 sm:flex sm:items-end">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              {CustomerContactListResource.name}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {CustomerContactListResource.description}
            </p>
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-3">
            <AuthShield access={["read-customercontacttypes"]}>
              <Button
                variant="info"
                border
                type="link"
                href="/sales/settings/customer-contact-types"
              >
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Customer contact types
              </Button>
            </AuthShield>
            <AuthShield access={["create-customers"]}>
              <Button type="link" href="/sales/settings/customer-contacts/add">
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Add customer contact
              </Button>
            </AuthShield>
          </div>
        </div>

        <TableCursor
          data={customerContacts}
          columns={columns}
          loading={loading}
          totalRows={customerContacts.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No customer contacts found"
          fallbackText="You can add a new customer contact by clicking on add customer contact button."
        />
      </div>
    </>
  );
};

export default CustomerContactList;
export const CustomerContactListResource: ResourceProps = {
  name: "Customer Contacts",
  description: "A list of customer contacts",
  access: ["read-customers"],
  path: "customer-contacts",
};
